import { useQuery } from "@tanstack/react-query";

import {
  ITmpSession,
  ITmpSessionDetail,
  ITmpSessionEvent,
  ITmpSessionFilters,
  ITmpSessionPayload,
} from "../../../@types/admin/AdminTmpIntegration";
import { IPaginatedResponse } from "../../../@types/PaginatedResponse";
import { tmpIntegrationService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

export const TMP_SESSIONS_QUERY_KEY = "admin-tmp-sessions";

/**
 * One page of Transmural Platform sessions (EN-5203), filtered and paginated
 * on the server — the table is a live operational view, so it must not go
 * stale between visits (the global QueryClient disables refetch-on-mount).
 */
export function useTmpSessions(filters: ITmpSessionFilters) {
  const { data, isLoading, isError, isFetching, error, refetch } = useQuery<
    IPaginatedResponse<ITmpSession[]>
  >({
    queryKey: [TMP_SESSIONS_QUERY_KEY, filters],
    queryFn: async () =>
      ensureOk(await tmpIntegrationService.getSessions(filters)),
    refetchOnMount: "always",
  });

  return {
    sessions: data?.result ?? [],
    page: data,
    isLoading,
    isError,
    isFetching,
    error,
    refetch,
  };
}

/** Everything TMP sent for one order, for the drill-down modal. */
export function useTmpSessionDetail(telemonitoringId: string | null) {
  const { data, isLoading, isError, error } = useQuery<ITmpSessionDetail>({
    queryKey: [TMP_SESSIONS_QUERY_KEY, "detail", telemonitoringId],
    queryFn: async () =>
      ensureOk(await tmpIntegrationService.getSession(telemonitoringId!)),
    enabled: Boolean(telemonitoringId),
    refetchOnMount: "always",
  });

  return { detail: data, isLoading, isError, error };
}

/**
 * The order's history for the drill-down timeline. Keyed under the sessions
 * key, so Retry and a new completion link refresh it with the detail.
 */
export function useTmpSessionEvents(telemonitoringId: string | null) {
  const { data, isLoading, isError } = useQuery<ITmpSessionEvent[]>({
    queryKey: [TMP_SESSIONS_QUERY_KEY, "events", telemonitoringId],
    queryFn: async () =>
      ensureOk(await tmpIntegrationService.getSessionEvents(telemonitoringId!)),
    enabled: Boolean(telemonitoringId),
    refetchOnMount: "always",
  });

  return { events: data ?? [], isLoading, isError };
}

/** The raw TMP webhook body for one order, for the Payload modal. */
export function useTmpSessionPayload(telemonitoringId: string | null) {
  const { data, isLoading, isError, error } = useQuery<ITmpSessionPayload>({
    queryKey: [TMP_SESSIONS_QUERY_KEY, "payload", telemonitoringId],
    queryFn: async () =>
      ensureOk(
        await tmpIntegrationService.getSessionPayload(telemonitoringId!)
      ),
    enabled: Boolean(telemonitoringId),
    refetchOnMount: "always",
  });

  return { payload: data, isLoading, isError, error };
}
